import { useMemo } from "react";
import { SOCIAL_NETWORK, SocialName, METRICS_EVENTS } from "@cede/types";
import { DialogHeader } from "@/components/features/wallet/ConnectAccount/DialogHeader";
import { Button } from "@/components/ui/button";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useDevice } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useSocialAccounts } from "@/hooks/socials/useSocialAccounts";
import { cn } from "@/lib/utils";
import { SOCIALS } from "@/utils/social";
import { ExternalLink } from "lucide-react";

interface LinkSocialsStepProps {
  title: string;
  subtitle: string;
  requiredSocialAccounts: SocialName[];
  onNext: (selectedSocial?: string) => void;
  onBack: () => void;
}

export const LinkSocialsStep = ({ title, subtitle, requiredSocialAccounts, onNext, onBack }: LinkSocialsStepProps) => {
  const { isMobile } = useDevice();
  const { socialAccounts, addSocialAccount, isAddingSocialAccount, hasRequiredSocialAccounts } = useSocialAccounts();
  const { isTMA } = useTelegramContext();
  const { trackEvent } = useMetricsEvents();

  // For TMA, we only show the native Telegram social account (native connects automatically in tma)
  // For non-TMA, we show all social accounts except native Telegram (as there's regular Telegram account linking)
  const socialsList = useMemo(() => {
    return SOCIALS.filter((social) =>
      isTMA ? social.id === SOCIAL_NETWORK.NATIVE_TELEGRAM : social.id !== SOCIAL_NETWORK.NATIVE_TELEGRAM,
    );
  }, [isTMA]);

  const handleLinkSocial = (socialName: SocialName) => {
    // Track when user links a social account in connect account modal
    trackEvent(METRICS_EVENTS.CONNECT_SOCIAL_IN_CAMPAIGN_STEPPER_MODAL_LINK, {
      socialName,
    });

    addSocialAccount.mutate(socialName);
  };

  const renderSocialsList = () => (
    <div className="w-full bg-transparent md:bg-cede-bg-component md:backdrop-blur-xl md:border border-cede-border-container rounded-medium md:rounded-large p-0 md:p-1 lg:p-2">
      <div className="flex flex-col">
        {socialsList
          .filter((social) => requiredSocialAccounts.includes(social.id as SocialName))
          .map((social, index, arr) => {
            const userSocial = socialAccounts.find((sa) => sa.socialName === social.id);
            const isLoadingSocial = isAddingSocialAccount(social.id);
            const isLast = index === arr.length - 1;

            return (
              <div
                key={social.id}
                className={cn(
                  "flex items-center justify-between w-full relative",
                  "py-2 sm:py-2 md:py-4",
                  "px-0 md:px-4",
                  "min-h-[64px] md:min-h-0",
                  !isLast &&
                    "after:content-[''] after:absolute after:bottom-0 after:left-0 md:after:left-4 after:right-0 md:after:right-4 after:h-px after:bg-cede-border-container",
                )}
              >
                <div className="flex items-center gap-2.5 md:gap-2">
                  {social.icon}
                  <div>
                    <p className="text-base font-medium text-cede-text-primary">{social.name}</p>
                    {userSocial && (
                      <p className="text-sm text-cede-text-secondary">
                        {social.usernamePrefix}
                        {userSocial.username}
                      </p>
                    )}
                  </div>
                </div>
                {!social.autoLink ? (
                  <div className="flex gap-2.5 md:gap-1.5">
                    {userSocial ? (
                      <p className="text-sm font-medium text-cede-success">Linked</p>
                    ) : (
                      <Button
                        variant={isMobile ? "transparent" : "transparent-bordered"}
                        size="small"
                        rightIcon={!isLoadingSocial && !isMobile ? <ExternalLink className="w-4 h-4" /> : undefined}
                        className={cn("min-w-0 md:min-w-[100px]")}
                        onClick={() => handleLinkSocial(social.id)}
                        loading={isLoadingSocial}
                        disabled={isLoadingSocial}
                      >
                        Link
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2.5 md:gap-1.5">
                    <p className={cn("text-sm font-medium", userSocial ? "text-cede-success" : "text-cede-error")}>
                      {userSocial ? "Linked" : "Auto-link failed"}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );

  const hasLinkedSocials = hasRequiredSocialAccounts(requiredSocialAccounts);

  return (
    <>
      {isMobile ? (
        <div className="flex flex-col w-full h-full">
          <DialogHeader title={title} showBackButton onBack={onBack} />

          <div className="flex flex-col flex-1 min-h-0 px-2 py-3 gap-3">
            <p className="text-base text-center text-cede-text-primary">{subtitle}</p>
            <div className="flex-1 overflow-y-auto">{renderSocialsList()}</div>
          </div>

          <div className="flex flex-col gap-3 px-2 pb-2 shrink-0">
            <Button
              size="large"
              variant="primary"
              className="w-full"
              onClick={() => onNext()}
              disabled={!hasLinkedSocials}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-[600px] relative flex flex-col h-full">
          <DialogHeader title={title} />

          <div className="flex flex-col items-center gap-6 flex-1 min-h-0 px-6 mt-6">
            {subtitle && <p className="text-base text-center max-w-[450px] text-cede-text-primary">{subtitle}</p>}

            <div className="w-full max-w-[500px] flex-1 overflow-y-auto">{renderSocialsList()}</div>

            <div className="flex gap-3 w-full justify-end items-center shrink-0">
              <Button variant="primary" size="medium" onClick={() => onNext()} disabled={!hasLinkedSocials}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
